import { SnapshotLabelHtmlClassNames } from "@/animation/video-generator";
import { ToolLoadingStateProvider } from "@/grid/ToolLoadingStateContext";
import { useExportStatus } from "@/hooks/useExportStatus";
import { classNames } from "@/utility/jsx";
import Logger from "@mm/logging";
import type { CrossSectionSchemaOutput, GridCellLayout } from "@mm/metx-workbench.meteomatics.com";
import { Suspense, lazy, memo } from "react";

const logger = Logger.fromFilename(__filename);

export type GridCrossSectionProps = {
  crossSection: CrossSectionSchemaOutput;
  cellLayout: GridCellLayout;
};

export const CrossSectionPlotLazy = memo(
  lazy(() =>
    import("@/crossSectionView/index")
      .then(({ CrossSectionPlot }) => ({ default: CrossSectionPlot }))
      .catch((e) => {
        logger.error("Failed to load Cross Section view", e);
        return { default: () => <div>Loading Cross Section View failed</div> };
      }),
  ),
);

const CrossSectionOverlay = memo(
  lazy(() =>
    import("@/overlay/overlays/default/CrossSectionOverlay")
      .then(({ CrossSectionOverlay }) => ({ default: CrossSectionOverlay }))
      .catch((e) => {
        logger.error("Failed to load Cross Section overlay", e);
        return { default: () => <div>Loading Cross Section Overlay failed</div> };
      }),
  ),
);

export function GridCrossSection({ crossSection, cellLayout }: GridCrossSectionProps) {
  const { isVideoExportActive } = useExportStatus();

  if (!crossSection) {
    return null;
  }

  return (
    <ToolLoadingStateProvider>
      <div className="overlay">
        <div className="overlay__layer">
          <Suspense fallback={<mm-loader size="large" />}>
            <CrossSectionPlotLazy crossSection={crossSection} />
          </Suspense>
        </div>
        {isVideoExportActive ? null : (
          <div className={classNames("overlay__layer", "gui-overlay", SnapshotLabelHtmlClassNames.CrossSection)}>
            <Suspense fallback={null}>
              <CrossSectionOverlay crossSection={crossSection} cellLayout={cellLayout} />
            </Suspense>
          </div>
        )}
      </div>
    </ToolLoadingStateProvider>
  );
}
